import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CONFIG } from 'variables/config';
import { authConfig, integrationTitle } from 'services/integrations';
import {
    FormTheme, THEME_STYLE_FIELDS, themeStyleOf, fetchTheme, updateTheme, duplicateTheme, deleteTheme, createTheme, setTemplateTheme,
} from 'services/formThemes';
import {
    StyleFields, FormBuilderField, DESIGN_PRESETS, DEFAULT_STYLE, MiniFormSwatch, StyleControls, StylePreview, sampleFieldIds,
} from './designStudio';
import ThemeApplyPanel from './themeApplyPanel';

interface EditorTemplate extends StyleFields {
    _id: string;
    name: string;
    isUniversal?: boolean;
    fieldOrder?: string[];
    themeId?: string;
}

// A theme's fields as typed, blanks included — themeStyleOf drops '' and the editor would then show
// the default back in a text field the seller is clearing to retype.
const themeValues = (theme: FormTheme): Partial<StyleFields> =>
    Object.fromEntries(THEME_STYLE_FIELDS.filter((f) => typeof theme[f] === 'string').map((f) => [f, theme[f]]));

const pageTitle = 'font-newsreader text-[34px] font-medium leading-[1.1] tracking-[-0.02em] text-[#14110E]';
const linkButton = 'font-hanken text-[13.5px] font-semibold text-[#241C5C] hover:underline';

/**
 * The design editor, full page (the integration's Apparence tab only picks a theme):
 * - `/integrations/themes/:themeId` edits a shared theme — every integration and page using it follows.
 *   `?from=<templateId>` sends the back link to that integration's Apparence tab instead of Mes thèmes.
 * - `/integrations/:id/design` edits one integration's own design ("Design propre").
 * Both autosave shortly after the last change, like the rest of the integration screens.
 */
const DesignEditor = () => {
    const navigate = useNavigate();
    const { themeId, id: templateId } = useParams<{ themeId?: string; id?: string }>();
    const [searchParams] = useSearchParams();
    const fromTemplateId = searchParams.get('from') || undefined;
    const isTheme = !!themeId;

    const [theme, setTheme] = useState<FormTheme | null>(null);
    const [template, setTemplate] = useState<EditorTemplate | null>(null);
    const [fromName, setFromName] = useState<string | null>(null);
    const [formFields, setFormFields] = useState<FormBuilderField[]>([]);
    const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
    const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [applyOpen, setApplyOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    // Set by user edits only, so a design that just came back from the server is never re-saved.
    const dirty = useRef(false);
    // What `persist` saves: the state of the last render, so a save fired on leaving the page (or
    // switching theme) still has the edits made just before.
    const latest = useRef({ isTheme, theme, template });
    latest.current = { isTheme, theme, template };

    // The whole style each time — a theme's blanks as '' so a cleared field is cleared server-side.
    const persist = async () => {
        const { isTheme: forTheme, theme: t, template: tpl } = latest.current;
        setSaveState('saving');
        try {
            if (forTheme && t) {
                await updateTheme(t._id, { style: Object.fromEntries(THEME_STYLE_FIELDS.map((f) => [f, t[f] || ''])) });
            } else if (!forTheme && tpl) {
                const style = Object.fromEntries(THEME_STYLE_FIELDS.filter((f) => tpl[f] !== undefined).map((f) => [f, tpl[f]]));
                await axios.put(`${CONFIG.apiUrl}/form-template/${tpl._id}`, style, authConfig());
            }
            setSaveState('saved');
        } catch (error) {
            console.error('Error saving design:', error);
            setSaveState('error');
            toast.error("Les modifications n'ont pas pu être enregistrées");
        }
    };

    const flush = async () => {
        if (!dirty.current) return;
        dirty.current = false;
        await persist();
    };

    // Leaving the page within the autosave delay must not drop the last edits.
    useEffect(() => () => { flush(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        axios.get(`${CONFIG.apiUrl}/form-field`).then((res) => setFormFields(res.data.results || [])).catch(() => {});
    }, []);

    useEffect(() => {
        // Switching to another theme (duplicate, saved as theme) on this same page: save the one being left.
        flush();
        if (isTheme) {
            fetchTheme(themeId!).then(setTheme).catch(() => {
                toast.error('Thème introuvable');
                navigate('/integrations/themes');
            });
        } else if (templateId) {
            axios.get(`${CONFIG.apiUrl}/form-template/${templateId}`, authConfig()).then((res) => setTemplate(res.data)).catch(() => {
                toast.error('Intégration introuvable');
                navigate('/integrations');
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTheme, themeId, templateId, navigate]);

    const backTemplateId = isTheme ? fromTemplateId : templateId;
    useEffect(() => {
        if (!isTheme || !fromTemplateId) return;
        axios.get(`${CONFIG.apiUrl}/form-template/${fromTemplateId}`, authConfig()).then((res) => setFromName(integrationTitle(res.data))).catch(() => {});
    }, [isTheme, fromTemplateId]);

    // Autosave shortly after the last change.
    useEffect(() => {
        if (!dirty.current) return;
        const timer = window.setTimeout(flush, 800);
        return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme, template, isTheme]);

    if ((isTheme && !theme) || (!isTheme && !template)) {
        return <div className="mx-auto max-w-[1140px] py-16 text-center font-hanken text-[14px] text-[#8A8172]">Chargement…</div>;
    }

    const values: StyleFields = isTheme ? { ...DEFAULT_STYLE, ...themeValues(theme!) } : (template as StyleFields);
    const previewFieldIds = !isTheme && template?.fieldOrder?.length ? template.fieldOrder : sampleFieldIds(formFields);

    const applyChanges = (updates: Partial<StyleFields>) => {
        dirty.current = true;
        if (isTheme) setTheme((t) => (t ? { ...t, ...updates } : t));
        else setTemplate((t) => (t ? { ...t, ...updates } : t));
    };

    const goBack = () => navigate(backTemplateId ? `/integrations/${backTemplateId}?tab=apparence` : '/integrations/themes');

    const startRename = () => {
        if (!theme) return;
        setNameDraft(theme.name);
        setIsEditingName(true);
    };
    const saveName = async () => {
        setIsEditingName(false);
        const name = nameDraft.trim().slice(0, 100);
        if (!theme || !name || name === theme.name) return;
        try {
            await updateTheme(theme._id, { name });
            setTheme({ ...theme, name });
        } catch {
            toast.error('Erreur lors du renommage du thème');
        }
    };

    const duplicate = async () => {
        if (!theme) return;
        try {
            await flush();
            const copy = await duplicateTheme(theme._id);
            toast.success(`Thème « ${copy.name} » créé`);
            navigate(`/integrations/themes/${copy._id}${fromTemplateId ? `?from=${fromTemplateId}` : ''}`);
        } catch {
            toast.error('Erreur lors de la duplication du thème');
        }
    };

    const remove = async () => {
        if (!theme) return;
        const uses = theme.usedBy?.length || 0;
        const warning = uses > 0 ? ` Il est utilisé par ${uses} formulaire${uses > 1 ? 's' : ''}, qui reprendront leur propre design.` : '';
        if (!window.confirm(`Supprimer le thème « ${theme.name} » ?${warning}`)) return;
        try {
            await deleteTheme(theme._id);
            toast.success('Thème supprimé');
            goBack();
        } catch {
            toast.error('Erreur lors de la suppression du thème');
        }
    };

    // "Design propre" → a theme: created from the current design and put on this integration in its place.
    const saveAsTheme = async () => {
        if (!template) return;
        const name = window.prompt('Nom du thème', template.name)?.trim();
        if (!name) return;
        try {
            await flush();
            const created = await createTheme(name.slice(0, 100), themeStyleOf(template));
            await setTemplateTheme(template._id, created._id);
            toast.success(`Thème « ${created.name} » créé et appliqué à ce formulaire`);
            navigate(`/integrations/themes/${created._id}?from=${template._id}`);
        } catch {
            toast.error("Le thème n'a pas pu être créé");
        }
    };

    const usedBy = theme?.usedBy || [];

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-2 pb-12 font-hanken">
            <ToastContainer position="bottom-right" autoClose={5000} />
            <button onClick={goBack} className="self-start font-hanken text-[13.5px] font-semibold text-[#241C5C] hover:underline">
                ← {backTemplateId ? (isTheme ? fromName || 'Retour au formulaire' : integrationTitle(template!)) : 'Mes thèmes'}
            </button>

            <div className="mt-1 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-[#A79E90]">{isTheme ? 'Thème' : 'Design propre'}</p>
                    {isTheme && isEditingName ? (
                        <input
                            autoFocus
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onBlur={saveName}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                if (e.key === 'Escape') setIsEditingName(false);
                            }}
                            className="w-full max-w-md border-b-2 border-[#241C5C] bg-transparent font-newsreader text-[34px] font-medium text-[#14110E] outline-none"
                        />
                    ) : isTheme ? (
                        <h1 className={`${pageTitle} cursor-text`} onClick={startRename} title="Cliquer pour renommer">{theme!.name}</h1>
                    ) : (
                        <h1 className={pageTitle}>{integrationTitle(template!)}</h1>
                    )}
                    <p className="mt-1.5 max-w-[720px] text-[13.5px] text-[#6B6357]">
                        {isTheme
                            ? usedBy.length === 0
                                ? "Ce thème n'est encore utilisé nulle part."
                                : `Utilisé sur : ${usedBy.map((u) => `${u.name}${u.templateWide ? '' : ` (${u.pages.length} page${u.pages.length > 1 ? 's' : ''})`}`).join(', ')}. Les modifications s'appliquent partout.`
                            : 'Ce design ne vaut que pour ce formulaire, sur les pages qui n\'ont pas de thème.'}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                    <span className="text-[12.5px] text-[#8A8172]" aria-live="polite">
                        {saveState === 'saving' ? 'Enregistrement…' : saveState === 'saved' ? 'Enregistré ✓' : saveState === 'error' ? 'Non enregistré' : ''}
                    </span>
                    {isTheme ? (
                        <>
                            <button type="button" onClick={duplicate} className={linkButton}>Dupliquer</button>
                            <button type="button" onClick={remove} className="font-hanken text-[13.5px] font-semibold text-red-600 hover:underline">Supprimer</button>
                            <button type="button" onClick={() => setApplyOpen(true)} className="rounded-[10px] bg-[#241C5C] px-4 py-2 text-[13.5px] font-semibold text-white hover:opacity-90">
                                Appliquer à…
                            </button>
                        </>
                    ) : (
                        <button type="button" onClick={saveAsTheme} className="rounded-[10px] bg-[#241C5C] px-4 py-2 text-[13.5px] font-semibold text-white hover:opacity-90">
                            Enregistrer comme thème
                        </button>
                    )}
                </div>
            </div>

            {!isTheme && template?.themeId && (
                <div className="mt-3 rounded-[14px] bg-[#FCF2D4] px-4 py-3 text-[13.5px] text-[#7A5A05]">
                    Ce formulaire utilise un thème : ce design propre ne s'affiche pas tant que le thème est appliqué.{' '}
                    <button type="button" onClick={() => navigate(`/integrations/themes/${template.themeId}?from=${template._id}`)} className="font-semibold underline">
                        Modifier le thème à la place
                    </button>
                </div>
            )}

            <div className="mt-4 overflow-hidden rounded-[18px] border border-[#EDE7DE] bg-white">
                <div className="border-b border-[#EDE7DE] px-6 py-4">
                    <p className="mb-3 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#A79E90]">Partir d'un modèle</p>
                    <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                        {DESIGN_PRESETS.map((preset) => (
                            <button key={preset.id} type="button" onClick={() => { setSelectedPreset(preset.id); applyChanges({ ...preset.style }); }}
                                className={`flex-shrink-0 rounded-xl border-2 p-3 text-left transition-all duration-200 hover:scale-[1.04] hover:shadow-md ${selectedPreset === preset.id ? 'border-[#241C5C] shadow-md' : 'border-[#EDE7DE] hover:border-[#241C5C]/50'}`}
                                style={{ minWidth: 96 }}
                            >
                                <MiniFormSwatch {...preset.preview} />
                                <p className="text-xs font-semibold leading-tight text-[#14110E]">{preset.name}</p>
                                <p className="mt-0.5 text-[10px] leading-tight text-[#8A8172]">{preset.description}</p>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-1 divide-y divide-[#EDE7DE] lg:grid-cols-[minmax(340px,420px)_1fr] lg:divide-x lg:divide-y-0">
                    <StyleControls values={values} onChange={(field, value) => applyChanges({ [field]: value })} />
                    <StylePreview values={values} previewFieldIds={previewFieldIds} formFields={formFields} />
                </div>
            </div>

            {applyOpen && theme && (
                <ThemeApplyPanel
                    theme={theme}
                    focusTemplateId={fromTemplateId}
                    onClose={() => setApplyOpen(false)}
                    onApplied={(next) => setTheme({ ...theme, usedBy: next })}
                />
            )}
        </div>
    );
};

export default DesignEditor;
